import {
  NodeVolumeRecordDto,
  WearDataDto,
  InspectionPointDto,
  PagedListOfWearDataDto,
  SpoolThroughputSnapshot
} from 'providers/api';
import { LocalDate } from '@js-joda/core';
import { PipeDisplayMode } from '../../Components/SceneLegend/legendUtils';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { Model } from '../../hooks/useModelState';

// A throughput record is a timestamp plus every spool's cumulative totals at that instant. One is
// written whenever a wear measurement is filed or a spool's history is reset, so the series across
// all records is the tonnage-versus-time history a wear rate is read from.

export const findRecordByDate = (throughputRecords: NodeVolumeRecordDto[], wearData: WearDataDto) => {
  const wearDataUtc = wearData.inspectionDate.toInstant();

  const exactMatch = throughputRecords.find((record) => {
    if (!record.generated) return false;

    const recordUtc = record.generated;
    const wearDataLocalDate = LocalDate.ofInstant(wearDataUtc);
    const recordLocalDate = LocalDate.ofInstant(recordUtc);

    return wearDataLocalDate.year() === recordLocalDate.year()
      && wearDataLocalDate.monthValue() === recordLocalDate.monthValue()
      && wearDataLocalDate.dayOfMonth() === recordLocalDate.dayOfMonth();
  });

  if (exactMatch) return exactMatch;

  const fallbackRecord = throughputRecords
    .filter((record) => {
      if (!record.generated) return false;
      const recordUtc = record.generated;
      return recordUtc.isBefore(wearDataUtc);
    })
    .sort((a, b) => {
      const dateA = a.generated!;
      const dateB = b.generated!;
      return dateB.compareTo(dateA);
    })[0];

  return fallbackRecord || null;
};

export const findSpoolSnapshot = (record: NodeVolumeRecordDto, spoolId: string): SpoolThroughputSnapshot | undefined => record
  .spoolSnapshots?.find((snapshot) => snapshot.spoolId === spoolId);

export const calculateWearRateBetweenPointsFor = (
  currentWearData: WearDataDto,
  previousWearData: WearDataDto,
  throughputRecords: NodeVolumeRecordDto[],
  spoolId: string,
  getThickness: (wear: WearDataDto) => number | null | undefined,
): number | null => {
  const currentThickness = getThickness(currentWearData);
  const previousThickness = getThickness(previousWearData);
  if (currentThickness == null || previousThickness == null) return null;

  const thicknessLoss = previousThickness - currentThickness;
  if (thicknessLoss <= 0) return null;

  const currentRecord = findRecordByDate(throughputRecords, currentWearData);
  const previousRecord = findRecordByDate(throughputRecords, previousWearData);
  if (!currentRecord || !previousRecord) return null;

  const currentSnapshot = findSpoolSnapshot(currentRecord, spoolId);
  const previousSnapshot = findSpoolSnapshot(previousRecord, spoolId);
  if (currentSnapshot?.totalDryTonnesThroughSpool == null || previousSnapshot?.totalDryTonnesThroughSpool == null) return null;

  const tonnageDifference = currentSnapshot.totalDryTonnesThroughSpool - previousSnapshot.totalDryTonnesThroughSpool;
  if (tonnageDifference <= 0) return null;

  return (thicknessLoss / tonnageDifference) * 1000000; // mm/Mt
};

// An inspection point measures a spool, so its wear rate is filed under that spool's pipe. A legacy
// point the migration could not relink has no spool and contributes nothing until one is chosen.
const getPipeIdForInspectionPoint = (inspectionPoint: InspectionPointDto, mineModel: Model): string | null => {
  if (!inspectionPoint.spoolId) return null;
  const spool = mineModel.spools[inspectionPoint.spoolId];
  return spool?.pipe?.pipeId || null;
};

export const useCalculateWearPerPipe = (
  useMostRecentOnly: boolean = false,
  inspectionPoints?: InspectionPointDto[],
  mineModel?: Model,
  allThroughputRecords?: { items: NodeVolumeRecordDto[] },
  allWearData?: PagedListOfWearDataDto,
): { [pipeId: string]: number } => {
  if (!inspectionPoints || !mineModel || !allWearData?.items || !allThroughputRecords?.items) {
    return {};
  }

  const wearRatesByPipe: { [pipeId: string]: number[] } = {};

  inspectionPoints.forEach((inspectionPoint: InspectionPointDto) => {
    const pipeId = getPipeIdForInspectionPoint(inspectionPoint, mineModel);
    const { spoolId } = inspectionPoint;
    if (!pipeId || !spoolId) return;

    const wearDataForPoint = allWearData.items.filter(
      (wearData: WearDataDto) => wearData.inspectionPointId === inspectionPoint.entityId,
    );

    if (wearDataForPoint.length < 2) return;

    const sortedWearData = [...wearDataForPoint].sort((a: WearDataDto, b: WearDataDto) => b.inspectionDate.toInstant().compareTo(a.inspectionDate.toInstant()));

    const pairs = useMostRecentOnly
      ? [[sortedWearData[0], sortedWearData[1]]]
      : sortedWearData.slice(0, -1).map((current, index) => [current, sortedWearData[index + 1]]);

    const wearRates = pairs
      .map(([current, previous]) => calculateWearRateBetweenPointsFor(
        current,
        previous,
        allThroughputRecords.items,
        spoolId,
        (wear) => wear.average ?? null,
      ))
      .filter((rate): rate is number => rate !== null);

    if (wearRates.length > 0) {
      wearRatesByPipe[pipeId] = [...(wearRatesByPipe[pipeId] ?? []), ...wearRates];
    }
  });

  const averageWearRatesByPipe: { [pipeId: string]: number } = {};

  Object.entries(wearRatesByPipe).forEach(([pipeId, rates]) => {
    if (rates.length > 0) {
      const average = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
      averageWearRatesByPipe[pipeId] = average;
    }
  });

  return averageWearRatesByPipe;
};

const colourFromMinMax = (value: number, minValue: number, maxValue: number): string => {
  if (!Number.isFinite(value) || !Number.isFinite(minValue) || !Number.isFinite(maxValue) || maxValue <= minValue) return '#ffffff';
  const t = Math.max(0, Math.min(1, (value - minValue) / (maxValue - minValue)));
  const red = Math.round(255 * t);
  const green = Math.round(255 * (1 - t));
  return `rgb(${red}, ${green}, 0)`;
};

// Tonnes through each spool, keyed by spool id - the live totals the model was loaded with.
export const spoolTonnesById = (spools: PCSpool[]): Record<string, number> => spools
  .reduce<Record<string, number>>((acc, spool) => ({ ...acc, [spool.spoolId]: spool.totalDryTonnesThroughSpool ?? 0 }), {});

export const generatePipeColour = (
  spools: PCSpool[],
  displayMode: PipeDisplayMode,
  pipeWearByPipeId?: Record<string, number>, // mm/Mt
): Record<string, string> => {
  const colourDictionary: { [key: string]: string } = {};
  if (!spools || spools.length === 0) return colourDictionary;

  if (displayMode === 'pipeType') {
    spools.forEach((spool) => { colourDictionary[spool.spoolId] = spool.spoolColour; });
    return colourDictionary;
  }

  const valuesBySpoolKey: Record<string, number> = {};

  spools.forEach((spool) => {
    const key = spool.spoolId;
    const { pipe } = spool;
    const tonnesThroughSpool = spool.totalDryTonnesThroughSpool ?? 0;

    if (displayMode === 'wearRate') {
      const wearRate = pipeWearByPipeId?.[spool.pipe.pipeId];
      if (wearRate) valuesBySpoolKey[key] = wearRate;
      return;
    }

    if (displayMode === 'throughputTonnes') {
      if (tonnesThroughSpool) valuesBySpoolKey[key] = tonnesThroughSpool;
      return;
    }

    if (!pipe?.startingThickness || !(pipeWearByPipeId && pipeWearByPipeId[spool.pipe.pipeId] != null) || !tonnesThroughSpool) {
      return;
    }

    const startingThicknessMm = pipe.startingThickness * 1000;
    const wearRateMmPerMt = pipeWearByPipeId[spool.pipe.pipeId];
    const wearMm = wearRateMmPerMt * (tonnesThroughSpool / 1000000);
    const remainingMm = startingThicknessMm - wearMm;

    if (displayMode === 'remainingThickness') {
      valuesBySpoolKey[key] = remainingMm;
    } else if (displayMode === 'remainingPercentage') {
      if (startingThicknessMm > 0) valuesBySpoolKey[key] = (remainingMm / startingThicknessMm) * 100;
    }
  });

  const usableValues = Object.values(valuesBySpoolKey).filter((v) => v != null && Number.isFinite(v));
  const minValue = 0;
  const maxValue = usableValues.length ? Math.max(...usableValues) : 0;

  spools.forEach((spool) => {
    const key = spool.spoolId;
    const value = valuesBySpoolKey[key];
    const effectiveValue = value != null && Number.isFinite(value) ? value : 0;
    colourDictionary[key] = colourFromMinMax(effectiveValue, minValue, maxValue);
  });

  return colourDictionary;
};
